/**
 * De gegevens die een kraam zelf invult voordat er uitbetaald kan worden.
 *
 * Het bouwplan (deel 4, "aanleveren vóór het evenement"): KvK-nummer,
 * BTW-nummer, IBAN en een contactpersoon met telefoonnummer. Zonder IBAN valt
 * de kraam uit het SEPA-bestand, zonder KvK en BTW-nummer mag er geen
 * inkoopfactuur (selfbilling) op zijn naam. De kraam vult dit zelf in op het
 * scherm met zijn kraamcode — dan weet je dat het klopt (Jonathan,
 * 18 september 2026), en niet de organisator die daarmee zou bepalen waar het
 * geld van de kraam heen gaat.
 */

import { isGeldigBtwNummer, isGeldigKvkNummer, normaliseerBtwNummer } from './bedrijfsgegevens.js';
import { controleerIban } from './iban.js';

export interface Kraamgegevens {
  iban: string;
  kvkNummer: string;
  btwNummer: string;
  contactpersoon: string;
  telefoon: string;
  email: string;
  /** Akkoord dat Quality Cashless de inkoopfactuur namens de kraam opmaakt. */
  selfbillingAkkoord: boolean;
}

export type KraamgegevensVeld = keyof Kraamgegevens;

export interface KraamgegevensControle {
  geldig: boolean;
  /** Per veld één zin voor de gebruiker; leeg als alles klopt. */
  fouten: Partial<Record<KraamgegevensVeld, string>>;
  /** Wat er opgeslagen wordt: zonder spaties, hoofdletters waar de bank die verwacht. */
  genormaliseerd: Kraamgegevens;
}

export const LEGE_KRAAMGEGEVENS: Kraamgegevens = {
  iban: '',
  kvkNummer: '',
  btwNummer: '',
  contactpersoon: '',
  telefoon: '',
  email: '',
  selfbillingAkkoord: false,
};

const EMAIL_VORM = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function controleerKraamgegevens(invoer: Kraamgegevens): KraamgegevensControle {
  const fouten: Partial<Record<KraamgegevensVeld, string>> = {};

  const iban = controleerIban(invoer.iban);
  if (!iban.geldig) fouten.iban = iban.melding ?? 'Dit rekeningnummer klopt niet.';

  const kvkNummer = invoer.kvkNummer.replace(/[\s.-]/g, '');
  if (!isGeldigKvkNummer(kvkNummer)) fouten.kvkNummer = 'Een KvK-nummer bestaat uit acht cijfers.';

  const btwNummer = normaliseerBtwNummer(invoer.btwNummer);
  if (!isGeldigBtwNummer(btwNummer)) {
    fouten.btwNummer = 'Een BTW-nummer ziet eruit als NL123456789B01.';
  }

  const contactpersoon = invoer.contactpersoon.trim();
  if (contactpersoon === '') fouten.contactpersoon = 'Wie kunnen we bellen als er iets is?';

  const telefoon = invoer.telefoon.trim();
  if (telefoon.replace(/\D/g, '').length < 10) {
    fouten.telefoon = 'Vul een telefoonnummer in waarop we je tijdens het evenement bereiken.';
  }

  const email = invoer.email.trim().toLowerCase();
  if (!EMAIL_VORM.test(email)) fouten.email = 'Vul het e-mailadres in waar de factuur naartoe mag.';

  if (!invoer.selfbillingAkkoord) {
    fouten.selfbillingAkkoord =
      'Zonder akkoord op selfbilling kunnen we geen inkoopfactuur voor je opmaken, en dus niet uitbetalen.';
  }

  return {
    geldig: Object.keys(fouten).length === 0,
    fouten,
    genormaliseerd: {
      iban: iban.genormaliseerd,
      kvkNummer,
      btwNummer,
      contactpersoon,
      telefoon,
      email,
      selfbillingAkkoord: invoer.selfbillingAkkoord,
    },
  };
}

/** Staat er genoeg om te factureren én uit te betalen? */
export function isKraamCompleet(gegevens: Partial<Kraamgegevens> | null | undefined): boolean {
  if (gegevens == null) return false;
  return controleerKraamgegevens({ ...LEGE_KRAAMGEGEVENS, ...gegevens }).geldig;
}
